import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {NOTIFICATION_GROUP_TITLES, notificationErrorMessage} from "../api/cabinetNotifications";
import type {CabinetNotification} from "../api/cabinetNotifications";
import {useAuth} from "../auth";
import {publishNotificationUnread, useMerchantQuery} from "../hooks";
import {formatMerchantDate} from "../utils";
import type {NotificationScope} from "./notificationScope";

interface NotificationFeedProps {
  scope: NotificationScope;
  pageSize: number;
}

/** Лента уведомлений кабинета: открытая лента считается прочитанной. Её показывают колокольчик в шапке и страница истории. */
export function NotificationFeed({scope, pageSize}: NotificationFeedProps): JSX.Element {
  const {token} = useAuth();
  const {list, markSeen} = scope.api;
  const request = useCallback(
    (signal: AbortSignal) => list(token, {limit: pageSize}, signal),
    [list, token, pageSize],
  );
  const feed = useMerchantQuery(request, `notifications-feed:${pageSize}:${token ?? ""}`, {
    enabled: Boolean(token),
    formatError: notificationErrorMessage,
  });

  const [older, setOlder] = useState<CabinetNotification[]>([]);
  const [olderNext, setOlderNext] = useState<number | null | undefined>(undefined);
  const [moreBusy, setMoreBusy] = useState(false);
  const [moreError, setMoreError] = useState<string | null>(null);
  const [freshIds, setFreshIds] = useState<ReadonlySet<number>>(new Set());
  const markedRef = useRef(0);

  const head = feed.data;

  // Открытая лента считается прочитанной: «прочитано до» самого нового
  // загруженного id. Новые строки, пришедшие при опросе, отмечаются так же.
  useEffect(() => {
    if (!head) return;
    publishNotificationUnread(head.unreadCount);
    const newest = head.items[0]?.id;
    if (head.unreadCount > 0) {
      const ids = head.items.slice(0, head.unreadCount).map((item) => item.id);
      setFreshIds((prev) => new Set([...Array.from(prev), ...ids]));
    }
    if (newest === undefined || newest <= markedRef.current) return;
    markedRef.current = newest;
    markSeen(token, newest)
      .then((result) => publishNotificationUnread(result.unreadCount))
      .catch(() => {
        // Не отметилось — повторим на следующем опросе.
        markedRef.current = 0;
      });
  }, [head, token, markSeen]);

  const items = useMemo(() => {
    if (!head) return [];
    const seen = new Set<number>();
    return [...head.items, ...older].filter((item) => {
      if (seen.has(item.id)) return false;
      seen.add(item.id);
      return true;
    });
  }, [head, older]);

  const nextBeforeId = olderNext !== undefined ? olderNext : head?.nextBeforeId ?? null;

  const loadMore = async (): Promise<void> => {
    if (nextBeforeId === null) return;
    setMoreBusy(true);
    setMoreError(null);
    try {
      const page = await list(token, {beforeId: nextBeforeId, limit: pageSize});
      setOlder((prev) => [...prev, ...page.items]);
      setOlderNext(page.nextBeforeId);
    } catch (err) {
      setMoreError(notificationErrorMessage(err));
    } finally {
      setMoreBusy(false);
    }
  };

  return (
    <>
      {feed.error && (
        <div className="merchant-notify-error" role="alert" data-testid="notification-feed-error">
          <p>{feed.error}</p>
          <button className="btn btn-ghost" type="button" onClick={feed.refresh}>
            Повторить
          </button>
        </div>
      )}
      {!head && !feed.error && <p className="merchant-notify-muted">Загрузка…</p>}
      {head && items.length === 0 && (
        <p className="merchant-notify-muted" data-testid="notification-feed-empty">
          Пока уведомлений нет. Новые заявки и события по балансу появятся здесь.
        </p>
      )}
      {items.length > 0 && (
        <ul className="merchant-notify-feed" data-testid="notification-feed">
          {items.map((item) => {
            const href = scope.itemHref(item);
            return (
              <li
                key={item.id}
                className={`merchant-notify-item${freshIds.has(item.id) ? " is-new" : ""}`}
                data-testid="notification-item"
                data-notification-id={item.id}
                data-group={item.group}
              >
                <div className="merchant-notify-item-head">
                  <span className="badge merchant-notify-group" data-testid="notification-group">
                    {NOTIFICATION_GROUP_TITLES[item.group] ?? item.group}
                  </span>
                  <time dateTime={item.occurredAt}>{formatMerchantDate(item.occurredAt)}</time>
                </div>
                <h3 className="merchant-notify-title">{item.title}</h3>
                {item.body && <p className="merchant-notify-body">{item.body}</p>}
                {href && (
                  <a className="merchant-notify-link" href={href}>
                    Открыть заявку
                  </a>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {moreError && (
        <div className="alert alert-error" role="alert" data-testid="notification-more-error">
          {moreError}
        </div>
      )}
      {items.length > 0 && nextBeforeId !== null && (
        <div className="merchant-notify-actions">
          <button
            className="btn btn-ghost"
            type="button"
            onClick={() => void loadMore()}
            disabled={moreBusy}
            data-testid="notification-more"
          >
            {moreBusy ? "Загружаем…" : "Показать ещё"}
          </button>
        </div>
      )}
    </>
  );
}
